import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref
} from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type { ShokurekishoJobHistoryItem } from '@/composables/format-edit/useShokurekishoFormatData'

type FormLibWithCharCount = {
  charCount: (value: unknown) => number
}

type ElFormLike = {
  validate: () => Promise<unknown>
}

type ElInputLike = {
  resizeTextarea?: () => void
  $el?: { offsetHeight?: number }
}

export type ShokurekishoHistoryItemProps = {
  item: ShokurekishoJobHistoryItem
  index: number
  length: number
  descriptionPlaceholder?: string
  descriptionMaxLength?: number
  minYear?: number | string
  maxYear?: number | string
  activeHistoryItem?: string
}

export type ShokurekishoHistoryItemEmit = {
  (
    e: 'add-history-item',
    index: number,
    item?: ShokurekishoJobHistoryItem
  ): void
  (e: 'delete-history-item', index: number): void
  (e: 'move-history-item', params: { index: number; to: string }): void
  (
    e: 'add-project-history-item',
    index: number,
    item?: ShokurekishoJobHistoryItem
  ): void
  (e: 'delete-history-item-detail', index: number, index2: number): void
  (
    e: 'move-history-item-detail',
    params: { index: number; to: string },
    parentIndex: number
  ): void
}

export function useHistoryItemDescResize() {
  const desc = ref<ElInputLike | ElInputLike[] | null>(null)

  function resizeDesc() {
    nextTick(() => {
      const target = Array.isArray(desc.value) ? desc.value[0] : desc.value
      if (target && typeof target.resizeTextarea === 'function') {
        if (target.$el && target.$el.offsetHeight) target.resizeTextarea()
      }
    })
  }

  onMounted(() => {
    emitter.on('card-changed', resizeDesc)
  })
  onBeforeUnmount(() => {
    emitter.off('card-changed', resizeDesc)
  })

  return { desc }
}

export function useShokurekishoHistoryItem(
  props: ShokurekishoHistoryItemProps,
  emit: ShokurekishoHistoryItemEmit
) {
  const { formLib } = useHostServices()
  const formLibChars = formLib as typeof formLib & FormLibWithCharCount
  const historyItemForm = ref<ElFormLike | null>(null)
  const errors = ref<string[]>([])
  const { desc } = useHistoryItemDescResize()

  const editableItem = computed(() => props.item)
  const isActive = computed(() => props.activeHistoryItem === props.item.uuid)
  const computedMinYear = computed(() =>
    props.minYear ? Number(props.minYear) : 1980
  )
  const computedMaxYear = computed(() =>
    props.maxYear ? Number(props.maxYear) : new Date().getFullYear()
  )

  function charTooLong(value: unknown, max: number) {
    return formLibChars.charCount(value) > max
  }

  const rules = {
    year: [
      {
        validator: (
          _rule: unknown,
          value: unknown,
          callback: (err?: Error) => void
        ) => {
          if (charTooLong(value, 6)) callback(new Error('Please input digits'))
          else callback()
        },
        message: '年が長すぎます',
        trigger: ['change', 'blur']
      }
    ],
    month: [
      {
        validator: (
          _rule: unknown,
          value: unknown,
          callback: (err?: Error) => void
        ) => {
          if (charTooLong(value, 4)) callback(new Error('Please input digits'))
          else callback()
        },
        message: '月が長すぎます',
        trigger: ['change', 'blur']
      }
    ],
    description: [
      {
        validator: (
          _rule: unknown,
          value: unknown,
          callback: (err?: Error) => void
        ) => {
          if (charTooLong(value, props.descriptionMaxLength || 0)) {
            callback(new Error('Please input digits'))
          } else callback()
        },
        message:
          '一行におさまる文字数をオーバーしています。文章を短くするか、行を ＋ で追加して入力してください。',
        trigger: ['change', 'blur']
      }
    ]
  }

  function handleCommand(command: string) {
    if (command === 'addAbove') emit('add-history-item', props.index)
    else if (command === 'addBelow') emit('add-history-item', props.index + 1)
    else if (command === 'delete') emit('delete-history-item', props.index)
    else if (command === 'duplicate') {
      emit('add-history-item', props.index, props.item)
    } else if (command === 'moveUp') {
      emit('move-history-item', { index: props.index, to: 'up' })
    } else if (command === 'moveDown') {
      emit('move-history-item', { index: props.index, to: 'down' })
    } else if (command === 'addProject') {
      emit('add-project-history-item', props.index, props.item)
    }
  }

  function deleteDetail(index2: number) {
    emit('delete-history-item-detail', props.index, index2)
  }

  function moveUpDetail(index2: number) {
    emit('move-history-item-detail', { index: index2, to: 'up' }, props.index)
  }

  function moveDownDetail(index2: number) {
    emit('move-history-item-detail', { index: index2, to: 'down' }, props.index)
  }

  function changeCheckbox() {
    if (editableItem.value.isContinuing) {
      editableItem.value.endYear = ''
      editableItem.value.endMonth = ''
    }
  }

  function deactivateHistoryItem() {
    emitter.emit('deactivate-history-item')
  }

  onBeforeMount(() => {
    nextTick(() => {
      if (!historyItemForm.value) return
      historyItemForm.value
        .validate()
        .then()
        .catch(() => {})
    })
  })

  return {
    historyItemForm,
    desc,
    errors,
    editableItem,
    isActive,
    computedMinYear,
    computedMaxYear,
    rules,
    handleCommand,
    deleteDetail,
    moveUpDetail,
    moveDownDetail,
    changeCheckbox,
    deactivateHistoryItem
  }
}
